import type {
  BasicUser,
  InsurancePlanOption,
  InsuranceProviderOption,
  LoginCredentials,
  SignupForm,
  User,
  ConditionMasterOption,
  UserCondition,
  UserConditionInput,
  DocumentSharingProvider,
  DocumentCategoryOption,
  UserDocumentSharingPolicy,
  UserInsurance,
  UserInsuranceInput,
  UserMedication,
  UserMedicationInput,
  UserProfileCompletionStatus,
} from "@shared/types/User";
import axios from "axios";
import type { Document, DocumentSubType } from "@shared/types/document.types";
import apiClient from "@/shared/api/apiClient";
import { axiosInstance, normalizeApiUrl } from "@/shared/api/axios";
import { API_ROUTES, getScopedAuthApiBase, type AuthScope } from "@/constants/apiRoutes";
import { getAccessToken } from "@/shared/utils/token";

const PROFILE_CACHE_TTL_MS = 5000;

type AuthApiResponse = {
  message?: string;
  accessToken?: string;
  user?: User;
  requiresEmailVerification?: boolean;
  requiresMfa?: boolean;
  mfaToken?: string;
  challengeId?: string;
  channel?: "email" | "sms" | "whatsapp" | "push";
  maskedDestination?: string;
  expiresInSeconds?: number;
  trustedDeviceEnabled?: boolean;
  trustedDeviceDays?: number;
};

export type MfaRequiredResponse = {
  requiresMfa: true;
  mfaToken: string;
  challengeId: string;
  channel: "email" | "sms" | "whatsapp" | "push";
  maskedDestination: string;
  expiresInSeconds: number;
  trustedDeviceEnabled?: boolean;
  trustedDeviceDays?: number;
};

type StoredAuth = {
  user: User;
  token?: string;
  scope?: AuthScope;
};

const inFlightProfileRequests = new Map<string, Promise<User>>();
const profileCache = new Map<string, { user: User; expiresAt: number }>();

function normalizeOptionalString(value: string | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function normalizeDateString(value: string | undefined) {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const isoMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (isoMatch) {
    return isoMatch[1];
  }

  return trimmed;
}

function getStoredAuth(): StoredAuth | null {
  try {
    const raw = localStorage.getItem("confidacare_auth");
    return raw ? (JSON.parse(raw) as StoredAuth) : null;
  } catch {
    return null;
  }
}

function getStoredAccessToken() {
  return getStoredAuth()?.token ?? getAccessToken();
}

function getAuthBasePath(scope: AuthScope = "user") {
  return getScopedAuthApiBase(scope);
}

export async function loginUser(
  payload: LoginCredentials,
): Promise<{ user: BasicUser; token: string } | MfaRequiredResponse> {
  const scope = payload.scope ?? "user";
  const data = await apiClient.post<AuthApiResponse, LoginCredentials>(
    `${getAuthBasePath(scope)}/login`,
    payload,
  );

  if (data.requiresMfa && data.mfaToken && data.challengeId && data.channel && data.maskedDestination) {
    return {
      requiresMfa: true,
      mfaToken: data.mfaToken,
      challengeId: data.challengeId,
      channel: data.channel,
      maskedDestination: data.maskedDestination,
      expiresInSeconds: data.expiresInSeconds ?? 300,
      trustedDeviceEnabled: data.trustedDeviceEnabled,
      trustedDeviceDays: data.trustedDeviceDays,
    };
  }

  if (!data.accessToken) {
    throw new Error("Missing access token");
  }

  return {
    user: data.user as BasicUser,
    token: data.accessToken,
  };
}

export async function signupUser(
  form: SignupForm,
): Promise<{ user: User | null; token: string }> {
  const data = await apiClient.post<AuthApiResponse, SignupForm>(
    API_ROUTES.auth.signup,
    form,
  );

  return {
    user: data.user ?? null,
    token: data.accessToken ?? "",
  };
}

export async function updateUserProfile(updates: Partial<User>): Promise<User> {
  if (!getStoredAccessToken()) {
    throw new Error("Not authenticated");
  }

  const payload = {
    first_name: normalizeOptionalString(updates.first_name),
    last_name: normalizeOptionalString(updates.last_name),
    phone: normalizeOptionalString(updates.phone),
    date_of_birth: normalizeDateString(updates.date_of_birth),
    address: normalizeOptionalString(updates.address),
    address_line1: normalizeOptionalString(updates.address_line1),
    city: normalizeOptionalString(updates.city),
    state: normalizeOptionalString(updates.state),
    zip_code: normalizeOptionalString(updates.zip_code),
    latitude: updates.latitude,
    longitude: updates.longitude,
    preferred_name: normalizeOptionalString(updates.preferred_name),
    sex_assigned_at_birth: normalizeOptionalString(updates.sex_assigned_at_birth),
    gender_identity: normalizeOptionalString(updates.gender_identity),
    pronouns: normalizeOptionalString(updates.pronouns),
    preferred_language: normalizeOptionalString(updates.preferred_language),
    race_ethnicity: normalizeOptionalString(updates.race_ethnicity),
    sexual_orientation: normalizeOptionalString(updates.sexual_orientation),
    disability_status: normalizeOptionalString(updates.disability_status),
    veteran_status: normalizeOptionalString(updates.veteran_status),
    marital_status: normalizeOptionalString(updates.marital_status),
    occupation: normalizeOptionalString(updates.occupation),
    emergency_contact_name: normalizeOptionalString(updates.emergency_contact_name),
    emergency_contact_relationship: normalizeOptionalString(
      updates.emergency_contact_relationship,
    ),
    emergency_contact_phone: normalizeOptionalString(
      updates.emergency_contact_phone,
    ),
    profile_picture_url: normalizeOptionalString(updates.profile_picture_url),
    allow_sms_notification: updates.allow_sms_notification ?? undefined,
    allow_whatsapp_notification: updates.allow_whatsapp_notification ?? undefined,
    self_pay_preference: updates.self_pay_preference ?? undefined,
  };

  const data = await apiClient.put<{ user: User }, typeof payload>(
    API_ROUTES.user.profile,
    payload,
  );

  profileCache.set(data.user.id, {
    user: data.user,
    expiresAt: Date.now() + PROFILE_CACHE_TTL_MS,
  });

  return data.user;
}

export async function loginWithOAuthProvider(input: {
  provider: string;
  credential: string;
}) {
  const data = await apiClient.post<AuthApiResponse, typeof input>(
    API_ROUTES.auth.oauth,
    input,
  );

  return {
    user: data.user,
    token: data.accessToken,
  };
}

export async function getCurrentUser(
  token?: string,
  scope: AuthScope = "user",
): Promise<User> {
  if (token) {
    const response = await axios.get<{ user: User }>(
      normalizeApiUrl(`${getAuthBasePath(scope)}/me`),
      {
        baseURL: "/api",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
        timeout: 10000,
      },
    );

    return response.data.user;
  }

  const response = await axiosInstance.get<{ user: User }>(
    `${getAuthBasePath(scope)}/me`,
  );

  return response.data.user;
}

function getMfaVerifyRoute(scope: AuthScope) {
  if (scope === "admins") return API_ROUTES.admins.auth.mfaVerify;
  if (scope === "providers") return API_ROUTES.providerPortal.auth.mfaVerify;
  return API_ROUTES.auth.mfaVerify;
}

function getMfaResendRoute(scope: AuthScope) {
  if (scope === "admins") return API_ROUTES.admins.auth.mfaResend;
  if (scope === "providers") return API_ROUTES.providerPortal.auth.mfaResend;
  return API_ROUTES.auth.mfaResend;
}

export async function verifyMfaLogin(input: {
  scope: AuthScope;
  mfaToken: string;
  challengeId: string;
  code: string;
  trustDevice?: boolean;
}): Promise<{ user: BasicUser; token: string }> {
  const data = await apiClient.post<AuthApiResponse, Omit<typeof input, "scope">>(
    getMfaVerifyRoute(input.scope),
    {
      mfaToken: input.mfaToken,
      challengeId: input.challengeId,
      code: input.code,
      trustDevice: input.trustDevice ?? false,
    },
  );

  if (!data.accessToken || !data.user) {
    throw new Error("Unable to verify MFA.");
  }

  return {
    user: data.user as BasicUser,
    token: data.accessToken,
  };
}

export async function resendMfaLogin(input: {
  scope: AuthScope;
  mfaToken: string;
  challengeId: string;
  channel?: "email" | "sms" | "whatsapp" | "push";
}) {
  return apiClient.post<
    {
      message?: string;
      challengeId: string;
      channel: "email" | "sms" | "whatsapp" | "push";
      maskedDestination: string;
      expiresInSeconds: number;
    },
    Omit<typeof input, "scope">
  >(getMfaResendRoute(input.scope), {
    mfaToken: input.mfaToken,
    challengeId: input.challengeId,
    channel: input.channel,
  });
}

export async function refreshUserSession(
  scope: AuthScope = "user",
): Promise<{ user: User; token: string }> {
  const data = await axios.post<{
    accessToken?: string;
    user?: User;
  }>(
    normalizeApiUrl(`${getAuthBasePath(scope)}/refresh`),
    undefined,
    {
      baseURL: "/api",
      withCredentials: true,
      timeout: 10000,
    },
  );

  if (!data.data.accessToken || !data.data.user) {
    throw new Error("Refresh response missing auth data");
  }

  return {
    user: data.data.user,
    token: data.data.accessToken,
  };
}

export async function getCurrentUserProfileCompletion(): Promise<UserProfileCompletionStatus> {
  const data = await apiClient.get<UserProfileCompletionStatus>(
    API_ROUTES.user.profileCompletion,
  );

  return data;
}

export async function getUserProfileById(id: string): Promise<User> {
  const cachedProfile = profileCache.get(id);

  if (cachedProfile && cachedProfile.expiresAt > Date.now()) {
    return cachedProfile.user;
  }

  const existingRequest = inFlightProfileRequests.get(id);
  if (existingRequest) {
    return existingRequest;
  }

  const request = (async () => {
    const data = await apiClient.get<{ user: User }>(API_ROUTES.user.detail(id));
    profileCache.set(id, {
      user: data.user,
      expiresAt: Date.now() + PROFILE_CACHE_TTL_MS,
    });
    return data.user;
  })();

  inFlightProfileRequests.set(id, request);

  try {
    return await request;
  } finally {
    inFlightProfileRequests.delete(id);
  }
}

export async function logoutUser(scope: AuthScope = "user"): Promise<void> {
  const token = getStoredAccessToken();

  await apiClient.post(`${getAuthBasePath(scope)}/logout`, undefined, token
    ? {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    : undefined);
}

export async function uploadProfilePhoto(input: {
  file: File;
}): Promise<{ user: User; document: Document; accessUrl: string }> {
  const formData = new FormData();
  formData.append("file", input.file);

  const response = await axiosInstance.post<{
    user: User;
    document: Document;
    accessUrl: string;
  }>(API_ROUTES.user.profilePhoto, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
}

export async function createIdentityVerificationSession(): Promise<{
  sessionId: string;
  sessionToken?: string | null;
  verificationUrl?: string | null;
  status: string;
}> {
  const response = await axiosInstance.post<{
    sessionId: string;
    sessionToken?: string | null;
    verificationUrl?: string | null;
    status: string;
  }>(API_ROUTES.verification.session);

  return response.data;
}

export async function sendPhoneVerificationCode(): Promise<{ status: string; message: string }> {
  return apiClient.post(API_ROUTES.user.phoneVerification.send);
}

export async function confirmPhoneVerificationCode(
  code: string,
): Promise<{ message: string; user: User }> {
  return apiClient.post(API_ROUTES.user.phoneVerification.check, { code });
}

function normalizeInsuranceInput(input: UserInsuranceInput) {
  return {
    insurance_provider_id: normalizeOptionalString(
      input.insurance_provider_id ?? undefined,
    ),
    insurance_plan_id: normalizeOptionalString(
      input.insurance_plan_id ?? undefined,
    ),
    carrier_name_manual: normalizeOptionalString(input.carrier_name_manual ?? undefined),
    plan_name: normalizeOptionalString(input.plan_name),
    plan_name_manual: normalizeOptionalString(input.plan_name_manual ?? undefined),
    member_id: normalizeOptionalString(input.member_id),
    group_id: normalizeOptionalString(input.group_id),
    policy_holder_name: normalizeOptionalString(input.policy_holder_name ?? undefined),
    relationship_to_policy_holder: normalizeOptionalString(
      input.relationship_to_policy_holder ?? undefined,
    ),
    effective_date: normalizeDateString(input.effective_date ?? undefined),
    expiry_date: normalizeDateString(input.expiry_date ?? undefined),
    document_url: normalizeOptionalString(input.document_url),
  };
}

export async function searchInsuranceProviders(
  query: string,
  insuranceType?: InsuranceProviderOption["insurance_type"],
): Promise<InsuranceProviderOption[]> {
  const data = await apiClient.get<{ items: InsuranceProviderOption[] }>(
    API_ROUTES.user.insurance.providers,
    {
      params: {
        query,
        ...(insuranceType ? { insuranceType } : {}),
      },
    },
  );

  return data.items;
}

export async function getCurrentUserInsurance(): Promise<UserInsurance[]> {
  const data = await apiClient.get<{ items: UserInsurance[] }>(API_ROUTES.user.insurance.list);
  return data.items;
}

export async function getCurrentUserInsuranceDetail(
  insuranceId: string,
): Promise<UserInsurance> {
  const data = await apiClient.get<{ item: UserInsurance }>(
    API_ROUTES.user.insurance.item(insuranceId),
  );
  return data.item;
}

export async function searchInsurancePlans(input: {
  insuranceProviderId?: string;
  query?: string;
  active?: boolean;
}): Promise<InsurancePlanOption[]> {
  const data = await apiClient.get<{ items: InsurancePlanOption[] }>(
    API_ROUTES.user.insurance.plans,
    {
      params: {
        ...(input.insuranceProviderId ? { insuranceProviderId: input.insuranceProviderId } : {}),
        ...(input.query ? { query: input.query } : {}),
        ...(input.active !== undefined ? { active: input.active } : {}),
      },
    },
  );

  return data.items;
}

export async function createUserInsurance(
  input: UserInsuranceInput,
): Promise<UserInsurance> {
  const data = await apiClient.post<{ item: UserInsurance }, UserInsuranceInput>(
    API_ROUTES.user.insurance.list,
    normalizeInsuranceInput(input),
  );

  return data.item;
}

export async function updateUserInsurance(
  insuranceId: string,
  input: UserInsuranceInput,
): Promise<UserInsurance> {
  const data = await apiClient.put<{ item: UserInsurance }, UserInsuranceInput>(
    API_ROUTES.user.insurance.item(insuranceId),
    normalizeInsuranceInput(input),
  );

  return data.item;
}

export async function deleteUserInsurance(insuranceId: string): Promise<void> {
  await apiClient.delete(API_ROUTES.user.insurance.item(insuranceId));
}

export async function validateUserInsurance(
  insuranceId: string,
): Promise<UserInsurance> {
  const data = await apiClient.post<{ item: UserInsurance }>(
    API_ROUTES.user.insurance.validate(insuranceId),
  );

  return data.item;
}

export async function uploadInsuranceDocument(input: {
  insuranceId: string;
  file: File;
  subType?: DocumentSubType;
}): Promise<{ document: Document; accessUrl: string }> {
  const formData = new FormData();
  formData.append("file", input.file);

  if (input.subType) {
    formData.append("subType", input.subType);
  }

  const response = await axiosInstance.post<{ document: Document; accessUrl: string }>(
    API_ROUTES.user.insurance.document(input.insuranceId),
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
}

export async function uploadInsuranceCard(input: {
  insuranceId: string;
  side: "front" | "back";
  file: File;
}): Promise<{ document: Document; accessUrl: string }> {
  const formData = new FormData();
  formData.append("file", input.file);

  const response = await axiosInstance.post<{ document: Document; accessUrl: string }>(
    input.side === "front"
      ? API_ROUTES.user.insurance.frontCard(input.insuranceId)
      : API_ROUTES.user.insurance.backCard(input.insuranceId),
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
}

export async function getInsuranceDocumentAccess(input: {
  insuranceId: string;
  action?: "viewed" | "downloaded";
}): Promise<{ document: Document; accessUrl: string }> {
  const response = await axiosInstance.get<{ document: Document; accessUrl: string }>(
    API_ROUTES.user.insurance.document(input.insuranceId),
    {
      params: {
        action: input.action ?? "viewed",
      },
    },
  );

  return response.data;
}

function getFileNameFromContentDisposition(value: string | undefined) {
  if (!value) return null;

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = value.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];

  const plainMatch = value.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim() ?? null;
}

export async function fetchInsuranceDocumentBlob(input: {
  insuranceId: string;
  action?: "viewed" | "downloaded";
}): Promise<{ blob: Blob; fileName: string }> {
  const access = await getInsuranceDocumentAccess(input);
  const response = await axiosInstance.get<Blob>(access.accessUrl, {
    responseType: "blob",
  });

  const contentDisposition = String(response.headers["content-disposition"] ?? "");
  return {
    blob: response.data,
    fileName:
      getFileNameFromContentDisposition(contentDisposition) ??
      `insurance-card-${input.action === "downloaded" ? "download" : "view"}`,
  };
}

function normalizeConditionInput(input: UserConditionInput) {
  return {
    condition_id: normalizeOptionalString(input.condition_id) ?? "",
  };
}

function normalizeMedicationInput(input: UserMedicationInput) {
  return {
    medication: normalizeOptionalString(input.medication) ?? "",
  };
}

export async function getConditionOptions(): Promise<ConditionMasterOption[]> {
  const data = await apiClient.get<{ items: ConditionMasterOption[] }>(API_ROUTES.user.conditions.master);
  return data.items;
}

export async function getTopSearchSymptoms(limit = 5): Promise<string[]> {
  const data = await apiClient.get<{ items: Array<{ name: string; usageCount: number }> }>(
    API_ROUTES.user.conditions.topSearchSymptoms,
    { params: { limit } },
  );
  return data.items.map((item) => item.name);
}

export async function getCurrentUserConditions(): Promise<UserCondition[]> {
  const data = await apiClient.get<{ items: UserCondition[] }>(API_ROUTES.user.conditions.list);
  return data.items;
}

export async function createUserCondition(
  input: UserConditionInput,
): Promise<UserCondition> {
  const data = await apiClient.post<{ item: UserCondition }, UserConditionInput>(
    API_ROUTES.user.conditions.list,
    normalizeConditionInput(input),
  );

  return data.item;
}

export async function updateUserCondition(
  conditionId: string,
  input: UserConditionInput,
): Promise<UserCondition> {
  const data = await apiClient.put<{ item: UserCondition }, UserConditionInput>(
    API_ROUTES.user.conditions.item(conditionId),
    normalizeConditionInput(input),
  );

  return data.item;
}

export async function deleteUserCondition(conditionId: string): Promise<void> {
  await apiClient.delete(API_ROUTES.user.conditions.item(conditionId));
}

export async function getCurrentUserMedications(): Promise<UserMedication[]> {
  const data = await apiClient.get<{ items: UserMedication[] }>(API_ROUTES.user.medications.list);
  return data.items;
}

export async function createUserMedication(
  input: UserMedicationInput,
): Promise<UserMedication> {
  const data = await apiClient.post<{ item: UserMedication }, UserMedicationInput>(
    API_ROUTES.user.medications.list,
    normalizeMedicationInput(input),
  );

  return data.item;
}

export async function updateUserMedication(
  medicationId: string,
  input: UserMedicationInput,
): Promise<UserMedication> {
  const data = await apiClient.put<{ item: UserMedication }, UserMedicationInput>(
    API_ROUTES.user.medications.item(medicationId),
    normalizeMedicationInput(input),
  );

  return data.item;
}

export async function deleteUserMedication(medicationId: string): Promise<void> {
  await apiClient.delete(API_ROUTES.user.medications.item(medicationId));
}

export async function getDocumentSharingProviders(): Promise<DocumentSharingProvider[]> {
  const data = await apiClient.get<{ providers: DocumentSharingProvider[] }>(
    API_ROUTES.user.documentSharing.providers,
  );
  return data.providers;
}

export async function getDocumentSharingCategories(): Promise<DocumentCategoryOption[]> {
  const data = await apiClient.get<{ categories: DocumentCategoryOption[] }>(
    API_ROUTES.user.documentSharing.categories,
  );
  return data.categories;
}

export async function getDocumentSharingPolicies(
  providerId?: string,
): Promise<UserDocumentSharingPolicy[]> {
  const data = await apiClient.get<{ policies: UserDocumentSharingPolicy[] }>(
    API_ROUTES.user.documentSharing.policies,
    {
      params: providerId ? { providerId } : undefined,
    },
  );
  return data.policies;
}

export async function updateDocumentSharingPolicies(input: {
  providerId: string;
  policies: Array<{ documentCategory: string; isAllowed: boolean }>;
}): Promise<UserDocumentSharingPolicy[]> {
  const data = await apiClient.put<{ policies: UserDocumentSharingPolicy[] }, { policies: Array<{ documentCategory: string; isAllowed: boolean }> }>(
    API_ROUTES.user.documentSharing.policyProvider(input.providerId),
    { policies: input.policies },
  );
  return data.policies;
}


